import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { sendPasswordResetEmail } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import styles from 'AppRoutes/BeerStoreSuccess/styles';
import { auth } from 'services/firebase/client';
import { formatCurrencyBRL } from 'utils/currency';
import { GROWLER_DELIVERY_TIME } from 'utils/growlersFlow';

interface LocationState {
  orderId: string;
  orderNumber: string;
  total: number;
  items: any[];
  paymentId?: string;
  error?: string;
  cashbackCredited?: number;
  cashbackRedeemed?: number;
  userEmail?: string;
  needsPasswordSetup?: boolean;
}

const GrowlerSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const [passwordEmailStatus, setPasswordEmailStatus] = useState<
    'idle' | 'sent' | 'error'
  >('idle');

  useEffect(() => {
    if (!state?.orderId) {
      navigate('/growlers');
    }
  }, [state, navigate]);

  useEffect(() => {
    if (!state?.needsPasswordSetup || !state?.userEmail) {
      return;
    }

    let cancelled = false;

    const sendSetupEmail = async () => {
      try {
        await sendPasswordResetEmail(auth, state.userEmail!);
        if (!cancelled) {
          setPasswordEmailStatus('sent');
        }
      } catch (error) {
        console.error('Erro ao enviar e-mail de definição de senha:', error);
        if (!cancelled) {
          setPasswordEmailStatus('error');
        }
      }
    };

    void sendSetupEmail();

    return () => {
      cancelled = true;
    };
  }, [state?.needsPasswordSetup, state?.userEmail]);

  if (!state) {
    return null;
  }

  const {
    orderNumber,
    total,
    items,
    error,
    cashbackCredited = 0,
    userEmail,
  } = state;

  return (
    <Box sx={styles.container}>
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          <Card sx={styles.successCard}>
            <CardContent sx={styles.successContent}>
              <CheckCircleIcon sx={styles.successIcon} />
              <Typography variant="h4" sx={styles.successTitle}>
                Pedido Realizado com Sucesso!
              </Typography>
              <Typography variant="h6" sx={styles.orderNumber}>
                Pedido #{orderNumber}
              </Typography>
            </CardContent>
          </Card>

          {error && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {cashbackCredited > 0 && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Você ganhou R$ {formatCurrencyBRL(cashbackCredited)} de cashback
              {userEmail ? ` no e-mail ${userEmail}` : ''}.
              {passwordEmailStatus === 'sent' &&
                ' Enviamos um link para definir sua senha e usar o saldo nas próximas compras.'}
              {passwordEmailStatus === 'error' &&
                ' Use “Esqueci a senha” em /forgot-password com este e-mail para acessar o saldo.'}
              {passwordEmailStatus === 'idle' &&
                !state.needsPasswordSetup &&
                ' Faça login com este e-mail para usar o saldo nas próximas compras de growlers.'}
            </Alert>
          )}

          <Card sx={styles.summaryCard}>
            <CardContent>
              <Typography variant="h6" sx={styles.sectionTitle}>
                Resumo do Pedido
              </Typography>

              <Box sx={styles.orderInfo}>
                <Box sx={styles.infoRow}>
                  <Typography variant="body1">
                    <strong>Número do Pedido:</strong>
                  </Typography>
                  <Typography variant="body1">{orderNumber}</Typography>
                </Box>

                <Box sx={styles.infoRow}>
                  <Typography variant="body1">
                    <strong>Total Pago:</strong>
                  </Typography>
                  <Typography variant="body1" sx={styles.totalAmount}>
                    R$ {total.toFixed(2).replace('.', ',')}
                  </Typography>
                </Box>

                <Box sx={styles.infoRow}>
                  <Typography variant="body1">
                    <strong>Itens:</strong>
                  </Typography>
                  <Typography variant="body1">
                    {items?.length || 0} produto(s)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={styles.statusCard}>
            <CardContent>
              <Typography variant="h6" sx={styles.sectionTitle}>
                Próximos passos
              </Typography>
              <List sx={styles.statusList}>
                <ListItem sx={styles.statusItem}>
                  <ShoppingBagIcon sx={styles.statusIcon} />
                  <ListItemText
                    primary="Pedido confirmado"
                    secondary="Vamos preparar seus growlers para entrega."
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItem>
                <ListItem sx={styles.statusItem}>
                  <NotificationsIcon sx={styles.statusIcon} />
                  <ListItemText
                    primary="Prazo de entrega"
                    secondary={GROWLER_DELIVERY_TIME}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Box sx={styles.actionButtons}>
            <Button
              variant="contained"
              onClick={() => navigate('/growlers')}
              sx={styles.continueShoppingButton}
              size="large"
            >
              Voltar para growlers
            </Button>
            {cashbackCredited > 0 && (
              <Button
                variant="outlined"
                onClick={() => navigate('/forgot-password')}
                sx={{ ml: { sm: 2 }, mt: { xs: 2, sm: 0 } }}
                size="large"
              >
                Definir senha / Esqueci a senha
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GrowlerSuccess;
